import { useCallback, useEffect } from 'react'
import { useUnsavedChangesStore } from '@/store/unsavedChanges'

type Options = {
  isDirty: boolean
  message?: string
}

/**
 * Registers form dirty state with the shared leave-guard store and
 * attaches beforeunload. Navigation (Cancel, Sidebar, Header) goes through
 * `requestNavigation` / store `requestLeave`.
 */
export function useUnsavedChangesGuard({
  isDirty,
  message = 'Changes might not be saved',
}: Options) {
  const setDirty = useUnsavedChangesStore((s) => s.setDirty)
  const setMessage = useUnsavedChangesStore((s) => s.setMessage)
  const isDialogOpen = useUnsavedChangesStore((s) => s.isDialogOpen)
  const dialogMessage = useUnsavedChangesStore((s) => s.message)
  const requestLeave = useUnsavedChangesStore((s) => s.requestLeave)
  const confirmLeave = useUnsavedChangesStore((s) => s.confirmLeave)
  const cancelLeave = useUnsavedChangesStore((s) => s.cancelLeave)

  useEffect(() => {
    setDirty(isDirty)
    return () => {
      setDirty(false)
    }
  }, [isDirty, setDirty])

  useEffect(() => {
    setMessage(message)
  }, [message, setMessage])

  useEffect(() => {
    if (!isDirty) return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const requestNavigation = useCallback(
    (navigateFn: () => void) => {
      requestLeave(navigateFn)
    },
    [requestLeave],
  )

  return {
    isDialogOpen,
    dialogMessage,
    requestNavigation,
    confirmLeave,
    cancelLeave,
  }
}
